import React, { useRef, useEffect, useState } from 'react';
import type { HelpContent } from './help-content';

interface ContextualHelpPanelProps {
  content: HelpContent;
  isOpen: boolean;
}

/**
 * Animated expandable panel that shows contextual help for a credential field.
 * Slides open below the field with a 200ms ease-out animation.
 * Uses OCNexus orange left border accent and muted background.
 */
export const ContextualHelpPanel: React.FC<ContextualHelpPanelProps> = ({ content, isOpen }) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOpen && innerRef.current) {
      setHeight(innerRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen, content]);

  return (
    <div
      style={{
        overflow: 'hidden',
        transition: 'max-height 200ms ease-out, opacity 200ms ease-out',
        maxHeight: isOpen ? height + 16 : 0,
        opacity: isOpen ? 1 : 0,
      }}
      aria-hidden={!isOpen}
    >
      <div
        ref={innerRef}
        style={{
          marginTop: 8,
          padding: '12px 16px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderLeft: '3px solid var(--orange, #00E5FF)',
          borderRadius: '0 6px 6px 0',
          fontSize: 'var(--text-sm, 13px)',
          lineHeight: 1.6,
          color: 'var(--text-secondary, #a3a3a3)',
        }}
      >
        {/* Why we need this */}
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary, #e5e5e5)' }}>Why we need this: </span>
          {content.why}
        </div>

        {/* What it does */}
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary, #e5e5e5)' }}>What it does: </span>
          {content.what}
        </div>

        {/* How to get it — numbered steps */}
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary, #e5e5e5)' }}>How to get it:</span>
          <ol style={{ margin: '6px 0 0 0', paddingLeft: 20 }}>
            {content.steps.map((step, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{step.text}</li>
            ))}
          </ol>
        </div>

        {/* Direct link */}
        <div style={{ marginBottom: 10 }}>
          <a
            href={content.link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--orange, #00E5FF)',
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {content.link.label}
            <span style={{ fontSize: 11 }}>{'\u2197'}</span>
          </a>
        </div>

        {/* Security note */}
        <div style={{
          padding: '6px 10px',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 4,
          fontSize: 'calc(var(--text-sm, 13px) - 1px)',
          color: 'var(--text-muted, #737373)',
        }}>
          <span style={{ marginRight: 4 }}>{'\uD83D\uDD12'}</span>
          {content.security}
        </div>
      </div>
    </div>
  );
};
